/* Stacked wishlist (replaces the Wishlist Hero app, 5 Oct 2026).
 *
 * Lists live on the store server (shopify-xero server/wishlist-routes.js),
 * reached through the app proxy /apps/sh/wishlist/<action>. Shopify signs each
 * call and tells the server which customer is signed in; guests get a random id
 * kept in localStorage. This script keeps a local copy so the header count and
 * hearts show instantly, and only calls the server when it has to: on first
 * use, after sign-in/out, every 30 minutes while browsing, and on every change.
 *
 * Wishlist Hero lists carry over by themselves: a signed-in customer's by
 * customer id, a guest's by the old Wishlist Hero id still in their browser.
 *
 * Events: add_to_wishlist to GA4 (gtag, else dataLayer) and "Added to Wishlist"
 * to Klaviyo (recorded for browsers Klaviyo has identified).
 */
(function () {
  'use strict';
  var cfgEl = document.getElementById('sh-wl-config');
  if (!cfgEl || window.shWishlist) return;
  var CFG = {};
  try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var API = CFG.proxy || '/apps/sh/wishlist';
  var OWNER = CFG.customerId ? 'c:' + CFG.customerId : 'g';
  var LS_LIST = 'sh_wl', LS_GUEST = 'sh_wl_g';
  var STALE_MS = 30 * 60 * 1000;

  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }

  function guestId() {
    var g = lsGet(LS_GUEST);
    if (!g || !/^[A-Za-z0-9_-]{16,40}$/.test(g)) {
      var a = new Uint8Array(16);
      (window.crypto || window.msCrypto).getRandomValues(a);
      g = Array.prototype.map.call(a, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
      lsSet(LS_GUEST, g);
    }
    return g;
  }

  // Wishlist Hero kept its list id in localStorage (store.js, JSON-encoded).
  function oldWlhHash() {
    var raw = lsGet('wishlisthero_.wl-h');
    if (!raw) return null;
    try { raw = JSON.parse(raw); } catch (e) {}
    return typeof raw === 'string' && /^[A-Za-z0-9_-]{6,64}$/.test(raw) ? raw : null;
  }

  var state = { items: [], owner: OWNER, at: 0 };
  (function load() {
    try {
      var s = JSON.parse(lsGet(LS_LIST) || 'null');
      if (s && s.owner === OWNER && Array.isArray(s.items)) state = s;
    } catch (e) {}
  })();
  function save() { lsSet(LS_LIST, JSON.stringify(state)); }

  function has(p, v) {
    return state.items.some(function (i) { return String(i.p) === String(p) && (!v || !i.v || String(i.v) === String(v)); });
  }

  function call(action, body) {
    body = body || {};
    body.g = guestId();
    var wlh = oldWlhHash();
    if (wlh) body.wlh = wlh;
    return fetch(API + '/' + action, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (j) {
        if (j && Array.isArray(j.items)) {
          state = { items: j.items, owner: OWNER, at: Date.now() };
          save();
          render();
        }
        if (!r.ok) throw new Error(j && j.error || r.status);
        return j;
      });
    });
  }

  function needsSync() {
    if (OWNER !== 'g') return Date.now() - state.at > STALE_MS || state.owner !== OWNER;
    // Guests with nothing saved and no old Wishlist Hero list: nothing to fetch.
    if (!state.at && !state.items.length && !oldWlhHash()) return false;
    return Date.now() - state.at > STALE_MS;
  }

  /* ---------- product data (for the window and events) ---------- */
  var productCache = {};
  function product(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }
  function variantOf(pr, v) {
    if (!pr) return null;
    var found = null;
    pr.variants.forEach(function (x) { if (String(x.id) === String(v)) found = x; });
    return found || pr.variants.filter(function (x) { return x.available; })[0] || pr.variants[0];
  }
  function money(cents) {
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || CFG.currency || 'SGD';
    var n = cents / 100;
    var s = n % 1 === 0 ? String(n) : n.toFixed(2);
    return cur + ' ' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function img(src, w) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- events ---------- */
  function track(p, v, handle) {
    product(handle).then(function (pr) {
      var va = variantOf(pr, v);
      var price = va ? va.price / 100 : undefined;
      var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || CFG.currency || 'SGD';
      var item = {
        item_id: (va && va.sku) || String(v || p),
        item_name: pr ? pr.title : undefined,
        item_brand: pr ? pr.vendor : undefined,
        item_variant: va && va.title !== 'Default Title' ? va.title : undefined,
        price: price, quantity: 1
      };
      try {
        if (typeof window.gtag === 'function') {
          // Plain gtag reaches the GA4 properties the Google & YouTube app configures;
          // the main property (via Tag Manager) needs send_to.
          window.gtag('event', 'add_to_wishlist', { currency: cur, value: price, items: [item] });
          if (CFG.ga4) window.gtag('event', 'add_to_wishlist', { currency: cur, value: price, items: [item], send_to: CFG.ga4 });
        } else {
          (window.dataLayer = window.dataLayer || []).push({ event: 'add_to_wishlist', ecommerce: { currency: cur, value: price, items: [item] } });
        }
      } catch (e) {}
      try {
        var props = {
          ProductID: String(p), VariantID: v ? String(v) : null,
          ProductName: pr ? pr.title : null, Brand: pr ? pr.vendor : null,
          VariantName: item.item_variant || null, SKU: va ? va.sku : null,
          Price: price, ImageURL: pr && pr.featured_image ? img(pr.featured_image, 600) : null,
          URL: location.origin + '/products/' + handle + (v ? '?variant=' + v : ''),
          $value: price
        };
        if (window.klaviyo && typeof window.klaviyo.track === 'function') window.klaviyo.track('Added to Wishlist', props);
        else (window._learnq = window._learnq || []).push(['track', 'Added to Wishlist', props]);
      } catch (e) {}
    });
  }

  /* ---------- header count ---------- */
  function renderCount() {
    var n = state.items.length;
    document.querySelectorAll('[data-sh-wl-count]').forEach(function (el) {
      el.textContent = n ? String(n) : '';
      el.hidden = !n;
    });
  }

  /* ---------- product page heart ---------- */
  // The selected variant: ?variant= in the URL if it is one of this product's
  // variants, else the LAST [name="id"] input in the form. The first input keeps
  // the variant the page loaded with; the theme only updates the later ones.
  function btnVariant(btn) {
    var form = btn.closest('form') || document.getElementById(btn.getAttribute('data-form'));
    var known = (btn.getAttribute('data-variant-ids') || '').split(',').filter(Boolean);
    var fromUrl = new URLSearchParams(location.search).get('variant');
    if (fromUrl && known.indexOf(fromUrl) !== -1) return fromUrl;
    var inputs = form ? form.querySelectorAll('[name="id"]') : [];
    var last = inputs.length ? inputs[inputs.length - 1].value : '';
    return last || btn.getAttribute('data-variant-id') || '';
  }
  function renderButtons() {
    document.querySelectorAll('[data-sh-wl-btn]').forEach(function (btn) {
      var on = has(btn.getAttribute('data-product-id'), btnVariant(btn));
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
      btn.classList.toggle('is-on', on);
    });
  }

  function toggle(btn) {
    var p = btn.getAttribute('data-product-id');
    var h = btn.getAttribute('data-handle');
    var v = btnVariant(btn);
    var on = has(p, v);
    var before = state.items.slice();
    if (on) {
      state.items = state.items.filter(function (i) { return !(String(i.p) === String(p) && (!v || !i.v || String(i.v) === String(v))); });
    } else {
      state.items = [{ p: p, v: v, h: h, t: new Date().toISOString() }].concat(state.items);
    }
    render();
    call(on ? 'remove' : 'add', { p: p, v: v, h: h, src: btn.getAttribute('data-src') || 'pdp' }).then(function () {
      if (!on) { toast(h, v); track(p, v, h); }
    }).catch(function () {
      state.items = before; render();
      toastText('Sorry, that didn’t save. Please try again.');
    });
  }

  /* ---------- styles for the window + toast (loaded on first use) ---------- */
  var cssLoaded = false;
  function loadCss() {
    if (cssLoaded || !CFG.modalCss) return;
    cssLoaded = true;
    var l = document.createElement('link');
    l.rel = 'stylesheet'; l.href = CFG.modalCss;
    document.head.appendChild(l);
  }

  /* ---------- toast ---------- */
  var toastEl, toastTimer;
  function toastShell() {
    loadCss();
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'sh-wl-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
      toastEl.addEventListener('click', function (e) {
        if (e.target.closest('[data-sh-wl-toast-close]')) hideToast();
        if (e.target.closest('[data-sh-wl-toast-open]')) { e.preventDefault(); hideToast(); open(); }
      });
    }
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 5000);
    return toastEl;
  }
  function hideToast() { if (toastEl) toastEl.classList.remove('is-open'); }
  function toastText(msg) {
    var t = toastShell();
    t.innerHTML = '<div class="sh-wl-toast__bar"><span>' + esc(msg) + '</span><button type="button" data-sh-wl-toast-close aria-label="Close">&times;</button></div>';
    requestAnimationFrame(function () { t.classList.add('is-open'); });
  }
  function toast(handle, v) {
    product(handle).then(function (pr) {
      var va = variantOf(pr, v);
      var t = toastShell();
      var name = pr ? pr.title + (va && va.title !== 'Default Title' ? ' · ' + va.title : '') : 'Saved';
      t.innerHTML =
        '<div class="sh-wl-toast__bar"><span>Added to your wishlist</span><button type="button" data-sh-wl-toast-close aria-label="Close">&times;</button></div>' +
        '<div class="sh-wl-toast__body"><span class="sh-wl-toast__tick" aria-hidden="true"></span><span class="sh-wl-toast__name">' + esc(name) + '</span></div>' +
        '<div class="sh-wl-toast__foot"><a href="#" data-sh-wl-toast-open>View wishlist</a></div>';
      requestAnimationFrame(function () { t.classList.add('is-open'); });
    });
  }

  /* ---------- the wishlist window ---------- */
  var modal, lastFocus;
  var HEART = '<svg viewBox="0 0 18 16" width="18" height="16" aria-hidden="true"><path d="M9 15.2 7.8 14.1C3.6 10.3.9 7.8.9 4.8.9 2.4 2.8.5 5.2.5c1.4 0 2.7.6 3.8 1.7C10 1.1 11.4.5 12.8.5c2.4 0 4.3 1.9 4.3 4.3 0 3-2.7 5.5-6.9 9.3L9 15.2Z" fill="currentColor"/></svg>';
  var BIN = '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M5.5 1.5h5M2 3.5h12M3.5 3.5l.7 10.2c.1.7.6 1.3 1.3 1.3h5c.7 0 1.3-.6 1.3-1.3l.7-10.2M6.5 6.5v5.5M9.5 6.5v5.5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';

  function shell() {
    loadCss();
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'sh-wl-modal';
    modal.hidden = true;
    modal.innerHTML =
      '<div class="sh-wl-modal__back" data-sh-wl-close></div>' +
      '<div class="sh-wl-modal__box" role="dialog" aria-modal="true" aria-labelledby="sh-wl-title" tabindex="-1">' +
        '<div class="sh-wl-modal__head"><h2 id="sh-wl-title">' + HEART + '<span>Wishlist</span> <span class="sh-wl-modal__n" data-sh-wl-count></span></h2>' +
        '<button type="button" class="sh-wl-modal__x" data-sh-wl-close aria-label="Close wishlist"><svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true"><path d="M3 3l12 12M15 3 3 15" stroke="currentColor" stroke-width="1.6"/></svg></button></div>' +
        '<div class="sh-wl-modal__body" data-sh-wl-body></div>' +
        '<div class="sh-wl-modal__foot" data-sh-wl-foot></div>' +
      '</div>';
    document.body.appendChild(modal);
    modal.addEventListener('click', onModalClick);
    modal.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') {
        var f = modal.querySelectorAll('a[href],button:not([disabled])');
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    });
    return modal;
  }

  function open() {
    var m = shell();
    lastFocus = document.activeElement;
    m.hidden = false;
    document.documentElement.classList.add('sh-wl-lock');
    requestAnimationFrame(function () { m.classList.add('is-open'); m.querySelector('.sh-wl-modal__box').focus(); });
    renderCount();
    renderModal(true);
    // Always fresh when opened: another device may have changed the list.
    if (OWNER !== 'g' || state.items.length || oldWlhHash() || state.at) {
      call('list').catch(function () {});
    }
  }
  function close() {
    if (!modal || modal.hidden) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('sh-wl-lock');
    setTimeout(function () { modal.hidden = true; }, 200);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  var renderSeq = 0;
  function renderModal(loading) {
    if (!modal || modal.hidden) return;
    var body = modal.querySelector('[data-sh-wl-body]');
    var foot = modal.querySelector('[data-sh-wl-foot]');
    var items = state.items.slice();
    var seq = ++renderSeq;
    foot.innerHTML = OWNER === 'g'
      ? '<p class="sh-wl-modal__note"><a href="/account/login">Sign in</a> to keep your wishlist on all your devices.</p>'
      : '';
    if (!items.length) {
      body.innerHTML = loading && OWNER !== 'g'
        ? '<div class="sh-wl-empty"><p>Loading your wishlist…</p></div>'
        : '<div class="sh-wl-empty"><h3>Your wishlist is empty</h3><p>Start saving the pieces you love. Tap the heart on any product to add it here.</p><a class="sh-wl-btn-primary" href="/collections/new-in">Shop new in</a></div>';
      return;
    }
    body.innerHTML = '<ul class="sh-wl-grid">' + items.map(function (it) {
      return '<li class="sh-wl-card is-loading" data-p="' + esc(it.p) + '" data-v="' + esc(it.v || '') + '"><div class="sh-wl-card__img"></div><div class="sh-wl-card__txt"></div></li>';
    }).join('') + '</ul>';
    items.forEach(function (it) {
      product(it.h).then(function (pr) {
        if (seq !== renderSeq) return;
        var li = body.querySelector('.sh-wl-card[data-p="' + it.p + '"][data-v="' + (it.v || '') + '"]');
        if (!li) return;
        li.classList.remove('is-loading');
        if (!pr) {
          li.innerHTML = '<div class="sh-wl-card__txt"><p class="sh-wl-card__name">No longer available</p></div>' +
            '<div class="sh-wl-card__row"><span></span><button type="button" class="sh-wl-card__rm" data-sh-wl-rm aria-label="Remove from wishlist">' + BIN + '</button></div>';
          return;
        }
        var va = variantOf(pr, it.v);
        var url = '/products/' + pr.handle + (va ? '?variant=' + va.id : '');
        var src = (va && va.featured_image && va.featured_image.src) || pr.featured_image;
        var vt = va && va.title !== 'Default Title' ? va.title : '';
        var added = it.t ? new Date(String(it.t).replace(' ', 'T') + (/Z|\+/.test(it.t) ? '' : 'Z')) : null;
        var addedTxt = added && !isNaN(added) ? 'Added ' + added.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
        var avail = va && va.available;
        li.innerHTML =
          '<a class="sh-wl-card__img" href="' + url + '">' + (src ? '<img src="' + esc(img(src, 420)) + '" alt="' + esc(pr.title) + '" loading="lazy" width="208" height="208">' : '') + '</a>' +
          '<div class="sh-wl-card__txt">' +
            (pr.vendor ? '<p class="sh-wl-card__brand">' + esc(pr.vendor) + '</p>' : '') +
            '<a class="sh-wl-card__name" href="' + url + '">' + esc(pr.title) + '</a>' +
            (vt ? '<p class="sh-wl-card__var">' + esc(vt) + '</p>' : '') +
            (addedTxt ? '<p class="sh-wl-card__date">' + addedTxt + '</p>' : '') +
          '</div>' +
          '<div class="sh-wl-card__row"><span class="sh-wl-card__price">' + (va ? money(va.price) : '') +
            (va && va.compare_at_price > va.price ? ' <s>' + money(va.compare_at_price) + '</s>' : '') + '</span>' +
            '<button type="button" class="sh-wl-card__rm" data-sh-wl-rm aria-label="Remove ' + esc(pr.title) + ' from wishlist">' + BIN + '</button></div>' +
          (avail
            ? '<button type="button" class="sh-wl-card__add" data-sh-wl-cart="' + va.id + '">Add to basket</button>'
            : '<a class="sh-wl-card__add is-out" href="' + url + '">Sold out · get notified</a>');
      });
    });
  }

  function onModalClick(e) {
    if (e.target.closest('[data-sh-wl-close]')) { close(); return; }
    var rm = e.target.closest('[data-sh-wl-rm]');
    if (rm) {
      var li = rm.closest('.sh-wl-card');
      var p = li.getAttribute('data-p'), v = li.getAttribute('data-v');
      var before = state.items.slice();
      state.items = state.items.filter(function (i) { return !(String(i.p) === p && String(i.v || '') === v); });
      render();
      call('remove', { p: p, v: v, src: 'window' }).catch(function () { state.items = before; render(); });
      return;
    }
    var add = e.target.closest('[data-sh-wl-cart]');
    if (add) {
      var card = add.closest('.sh-wl-card');
      var id = add.getAttribute('data-sh-wl-cart');
      add.disabled = true;
      add.textContent = 'Adding…';
      call('cart', { p: card.getAttribute('data-p'), v: id }).catch(function () {});
      var done = function () { add.disabled = false; add.textContent = 'Add to basket'; };
      if (window.jQuery) {
        // The theme's jQuery cart hooks open and refresh the basket drawer.
        close();
        jQuery.ajax({ type: 'POST', url: '/cart/add.js', data: { id: id, quantity: 1 }, dataType: 'json' }).always(done);
      } else {
        fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: Number(id), quantity: 1 }) })
          .then(function () { location.href = '/cart'; }).catch(done);
      }
    }
  }

  function render() {
    renderCount();
    renderButtons();
    renderModal(false);
  }

  /* ---------- wiring ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('.js-wishlist-open, [data-sh-wl-open]');
    if (t) { e.preventDefault(); open(); return; }
    var b = e.target.closest('[data-sh-wl-btn]');
    if (b) { e.preventDefault(); toggle(b); }
  });
  // Variant changes on the product page change which variant the heart refers to.
  document.addEventListener('change', function (e) {
    if (e.target.closest('form[action*="/cart/add"]')) setTimeout(renderButtons, 60);
  });
  window.addEventListener('popstate', function () { setTimeout(renderButtons, 0); });

  // Sold out: the theme hides Add to basket (.cartButton.disabled) and the
  // back-in-stock app (Kbite) adds "Notify me" as its own block underneath,
  // which left the heart alone on a row above it. Keep the heart beside
  // whichever button is showing; it moves back when an in-stock variant is picked.
  function placeHeart() {
    document.querySelectorAll('.sh-wl-pdp').forEach(function (heart) {
      var form = heart.closest('form');
      var row = form && form.querySelector('.product-form-add-wr');
      if (!row) return;
      var add = row.querySelector('.cartButton');
      var notify = form.querySelector('.gw-button-widget-container');
      var useNotify = !!(add && add.classList.contains('disabled') && notify && notify.offsetParent !== null);
      var target = useNotify ? notify : row;
      if (heart.parentNode !== target) target.appendChild(heart);
      if (notify) notify.classList.toggle('sh-wl-with-heart', useNotify);
    });
  }
  var placeTimer = null;
  function schedulePlace() {
    clearTimeout(placeTimer);
    placeTimer = setTimeout(placeHeart, 0);
  }
  if ('MutationObserver' in window) {
    document.querySelectorAll('product-form').forEach(function (pf) {
      new MutationObserver(schedulePlace).observe(pf, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    });
  }
  schedulePlace();

  render();
  if (needsSync()) {
    var go = function () { call('list').catch(function () {}); };
    if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 3000 }); else setTimeout(go, 1500);
  }

  window.shWishlist = { open: open, close: close, has: has, items: function () { return state.items.slice(); } };
})();
